import { Component } from '@theme/component';

/**
 * Inline quick add component with variant selectors and add to cart button.
 * Supports color swatches (radio inputs) and size dropdowns (select elements).
 */
export class QuickAddComponent extends Component {
  /** @type {Array<{id: number, available: boolean, price: string, compareAtPrice: string | null, options: string[]}>} */
  #variants = [];

  connectedCallback() {
    super.connectedCallback();
    this.#parseVariantData();
  }

  /**
   * Parses variant data from the embedded JSON script tag.
   */
  #parseVariantData() {
    const dataEl = this.querySelector('script[data-variant-json]');
    if (!dataEl) return;

    try {
      this.#variants = JSON.parse(dataEl.textContent || '[]');
    } catch {
      // No variant data available
    }
  }

  /**
   * Previews the correct variant image when hovering a swatch.
   * Resolves the variant dynamically based on current selections + hovered value.
   * @param {Event} event - The pointerenter event on the swatch input.
   */
  previewSwatchVariant = (event) => {
    if (!this.#variants.length) return;

    const input = /** @type {HTMLInputElement} */ (event.target);
    const position = Number(input.dataset.optionPosition);
    if (Number.isNaN(position)) return;

    const selectedOptions = this.#getSelectedOptions();
    selectedOptions[position - 1] = input.value;

    const variant = this.#variants.find((v) => v.options.every((opt, i) => opt === selectedOptions[i]));
    if (!variant) return;

    const productCard = this.closest('product-card');
    if (!productCard) return;

    if (variant.mediaId && typeof productCard.previewVariant === 'function') {
      productCard.previewVariant(String(variant.mediaId));
    }

    if (typeof productCard.previewBadges === 'function') {
      productCard.previewBadges({
        available: variant.available,
        compareAtPrice: variant.compareAtPrice,
        badges: variant.badges || [],
        imageBadges: variant.imageBadges || [],
        soldOutColor: this.dataset.soldOutColor || '',
        soldOutText: this.dataset.soldOutText || 'Sold out',
      });
    }
  };

  /**
   * Handles variant selection change from both swatch radios and select dropdowns.
   * Collects all current option values (from radios and selects), finds the
   * matching variant, and updates the form, price, and button state.
   */
  handleVariantChange = () => {
    if (!this.#variants.length) return;

    const selectedOptions = this.#getSelectedOptions();
    const variant = this.#variants.find((v) => v.options.every((opt, i) => opt === selectedOptions[i]));

    if (!variant) return;

    this.#updateForm(variant);
    this.#updateProductCardLink(variant);
    this.#updateProductCardImage(variant);
    this.#updateBadges(variant);
  };

  /**
   * Collects selected option values from all inputs in option-position order.
   * Supports both radio inputs (swatches) and select elements (dropdowns).
   * @returns {string[]} Array of selected option values ordered by position.
   */
  #getSelectedOptions() {
    /** @type {Map<number, string>} */
    const optionsByPosition = new Map();

    // Collect from swatch radios (checked)
    const checkedRadios = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('.variant-compact__swatch-label input:checked')
    );
    for (const radio of checkedRadios) {
      const position = Number(radio.dataset.optionPosition);
      if (!Number.isNaN(position)) {
        optionsByPosition.set(position, radio.value);
      }
    }

    // Collect from selects
    const selects = /** @type {NodeListOf<HTMLSelectElement>} */ (
      this.querySelectorAll('.variant-compact__select')
    );
    for (const select of selects) {
      const position = Number(select.dataset.optionPosition);
      if (!Number.isNaN(position)) {
        optionsByPosition.set(position, select.value);
      }
    }

    // Build array ordered by position (1-based)
    const sorted = [...optionsByPosition.entries()].sort((a, b) => a[0] - b[0]);
    return sorted.map(([, value]) => value);
  }

  /**
   * Updates the hidden variant ID input, price display, button state, and notify state.
   * @param {{id: number, available: boolean, price: string, compareAtPrice: string | null}} variant
   */
  #updateForm(variant) {
    const variantInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (variantInput) {
      variantInput.value = String(variant.id);
      variantInput.disabled = !variant.available;
    }

    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('button.quick-add-inline__button'));
    const notifyLink = /** @type {HTMLAnchorElement | null} */ (this.querySelector('a.quick-add-inline__button'));

    if (variant.available) {
      if (button) button.hidden = false;
      if (notifyLink) notifyLink.hidden = true;

      const priceDisplay = button?.querySelector('.quick-add-inline__button-price');
      if (priceDisplay) priceDisplay.textContent = variant.price;

      const priceGroup = button?.querySelector('.quick-add-inline__button-price-group');
      const compareDisplay = priceGroup?.querySelector('.quick-add-inline__compare-price');
      if (variant.compareAtPrice) {
        if (compareDisplay) {
          compareDisplay.textContent = variant.compareAtPrice;
          compareDisplay.removeAttribute('hidden');
        } else if (priceGroup) {
          const span = document.createElement('span');
          span.className = 'compare-at-price quick-add-inline__compare-price';
          span.textContent = variant.compareAtPrice;
          priceGroup.insertBefore(span, priceGroup.firstChild);
        }
      } else if (compareDisplay) {
        compareDisplay.setAttribute('hidden', '');
      }
    } else {
      if (button) button.hidden = true;
      if (notifyLink) {
        notifyLink.hidden = false;
        const baseUrl = this.dataset.productUrl;
        if (baseUrl) notifyLink.href = `${baseUrl}?variant=${variant.id}`;
      }
    }
  }

  /**
   * Updates product card badges to reflect the selected variant.
   * Handles sold out, custom text badges, and image badges.
   * @param {{id: number, available: boolean, badges: Array<{title: string, bg: string, text: string}>, imageBadges: Array<{src: string, srcset: string, alt: string}>}} variant
   */
  #updateBadges(variant) {
    const productCard = this.closest('product-card');
    if (!productCard) return;

    const badgeContainer = productCard.querySelector('[ref="badgeContainer"]');
    const cardGallery = productCard.querySelector('[ref="cardGallery"]');
    if (!badgeContainer) return;

    // Build text badges
    const badgeHtml = [];

    if (!variant.available) {
      const colorClass = this.dataset.soldOutColor || '';
      badgeHtml.push(
        `<div class="product-badges__badge product-badges__badge--rectangle ${colorClass}">${this.dataset.soldOutText || 'Sold out'}</div>`
      );
    }

    if (variant.badges) {
      for (const badge of variant.badges) {
        badgeHtml.push(
          `<div class="product-badges__badge product-badges__badge--rectangle product-badges__badge--custom" style="--badge-custom-bg: ${badge.bg}; --badge-custom-text: ${badge.text};">${badge.title}</div>`
        );
      }
    }

    badgeContainer.innerHTML = badgeHtml.join('');

    // Build image badges
    let imageBadgeContainer = productCard.querySelector('[ref="imageBadgeContainer"]');
    const hasImageBadges = variant.imageBadges && variant.imageBadges.length > 0;

    if (hasImageBadges) {
      const imgHtml = variant.imageBadges
        .map((b) => `<img src="${b.src}" srcset="${b.srcset}" sizes="85px" alt="${b.alt}" loading="lazy" class="product-badges__image-badge" width="85" height="auto">`)
        .join('');

      if (imageBadgeContainer) {
        imageBadgeContainer.innerHTML = imgHtml;
      } else if (cardGallery) {
        const div = document.createElement('div');
        div.className = 'product-badges product-badges--top-right';
        div.setAttribute('ref', 'imageBadgeContainer');
        div.innerHTML = imgHtml;
        cardGallery.appendChild(div);
      }
    } else if (imageBadgeContainer) {
      imageBadgeContainer.remove();
    }

    // Update modifier classes on card gallery
    if (cardGallery) {
      cardGallery.classList.toggle('card-gallery--badge-top-left', badgeHtml.length > 0);
      cardGallery.classList.toggle('card-gallery--badge-top-right', hasImageBadges);
    }
  }

  /**
   * Updates the product card image to show the selected variant's media.
   * @param {{id: number, mediaId: number | null}} variant
   */
  #updateProductCardImage(variant) {
    if (!variant.mediaId) return;

    const productCard = this.closest('product-card');
    if (productCard && typeof productCard.setVariantMedia === 'function') {
      productCard.setVariantMedia(String(variant.mediaId));
    }
  }

  /**
   * Updates the product card link URL with the selected variant ID.
   * @param {{id: number}} variant
   */
  #updateProductCardLink(variant) {
    const productCard = this.closest('product-card');
    const link = /** @type {HTMLAnchorElement | null} */ (productCard?.querySelector('a[ref="productCardLink"]'));
    if (link?.href) {
      const url = new URL(link.href);
      url.searchParams.set('variant', String(variant.id));
      link.href = url.toString();
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}
